import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Clock, RotateCcw, Search, XCircle } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { PageHeader } from "../components/common/PageHeader";
import { reactivationRequestsAPI } from "../api";
import type { ReactivationRequest } from "../api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

type PendingAction = { type: "approve" | "reject"; request: ReactivationRequest } | null;

const STATUS_VARIANT: Record<
  ReactivationRequest["status"],
  { label: string; className: string }
> = {
  pending: { label: "Pending", className: "bg-amber-100 text-amber-800 border-amber-200" },
  approved: { label: "Restored", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  rejected: { label: "Rejected", className: "bg-rose-100 text-rose-800 border-rose-200" },
  expired: { label: "Expired", className: "bg-muted text-muted-foreground" },
};

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

/**
 * Days left before the account can no longer be brought back. Shown on every
 * pending row because it is the one thing that makes a request urgent — after
 * it lapses, approving is refused.
 */
const daysLeft = (deadline: string): number =>
  Math.max(0, Math.ceil((new Date(deadline).getTime() - Date.now()) / 86_400_000));

export function ReactivationRequestsPage() {
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [notes, setNotes] = useState("");

  const {
    data: requestsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["reactivation-requests", statusFilter, search],
    queryFn: () =>
      reactivationRequestsAPI.getReactivationRequests({
        status: statusFilter,
        search: search || undefined,
        limit: 50,
      }),
  });

  const { data: stats } = useQuery({
    queryKey: ["reactivation-request-stats"],
    queryFn: () => reactivationRequestsAPI.getStats(),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["reactivation-requests"] });
    queryClient.invalidateQueries({ queryKey: ["reactivation-request-stats"] });
    // The account is live again, so anything listing users is stale.
    queryClient.invalidateQueries({ queryKey: ["users"] });
  };

  const approveMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      reactivationRequestsAPI.approve(id, notes),
    onSuccess: () => {
      toast.success("Account restored");
      setPendingAction(null);
      setNotes("");
      refresh();
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      toast.error(err.response?.data?.error ?? "Failed to restore the account");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      reactivationRequestsAPI.reject(id, notes),
    onSuccess: () => {
      toast.success("Request rejected");
      setPendingAction(null);
      setNotes("");
      refresh();
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      toast.error(err.response?.data?.error ?? "Failed to reject the request");
    },
  });

  const confirmAction = () => {
    if (!pendingAction) return;

    if (pendingAction.type === "approve") {
      approveMutation.mutate({ id: pendingAction.request._id, notes: notes || undefined });
      return;
    }

    if (!notes.trim()) {
      toast.error("A reason is required to reject");
      return;
    }
    rejectMutation.mutate({ id: pendingAction.request._id, notes });
  };

  const requests = requestsData?.requests ?? [];
  const isBusy = approveMutation.isPending || rejectMutation.isPending;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Reactivation Requests"
          description="Deleted accounts asking to come back. Approving restores the account immediately."
        />

        <div className="grid gap-4 md:grid-cols-4">
          {(
            [
              { key: "pending", label: "Pending", icon: Clock },
              { key: "approved", label: "Restored", icon: CheckCircle2 },
              { key: "rejected", label: "Rejected", icon: XCircle },
              { key: "expired", label: "Expired", icon: RotateCcw },
            ] as const
          ).map(({ key, label, icon: Icon }) => (
            <Card key={key}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{label}</CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.[key] ?? 0}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="sm:w-48">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Restored</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message="Failed to load reactivation requests" />
        ) : requests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No reactivation requests here.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>School</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Window</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => {
                  const snapshot = request.user_snapshot;
                  const name =
                    [snapshot.first_name, snapshot.last_name].filter(Boolean).join(" ") ||
                    snapshot.email;
                  const remaining = daysLeft(request.recoverable_until);
                  const avatar =
                    typeof request.user_id === "object" ? request.user_id.avatar_url : undefined;

                  return (
                    <TableRow key={request._id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={avatar} alt={name} />
                            <AvatarFallback>{name.charAt(0).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="truncate font-medium">{name}</div>
                            <div className="truncate text-sm text-muted-foreground">
                              {snapshot.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {snapshot.school_id?.school_name ?? "—"}
                        {snapshot.campus_id ? (
                          <div className="text-muted-foreground">
                            {snapshot.campus_id.campus_name}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(request.createdAt)}</TableCell>
                      <TableCell className="text-sm">
                        {request.status === "pending" ? (
                          <span className={remaining <= 3 ? "font-medium text-rose-600" : ""}>
                            {remaining} day{remaining === 1 ? "" : "s"} left
                          </span>
                        ) : (
                          formatDate(request.recoverable_until)
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={STATUS_VARIANT[request.status].className}>
                          {STATUS_VARIANT[request.status].label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {request.status === "pending" ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                setNotes("");
                                setPendingAction({ type: "approve", request });
                              }}>
                              Restore
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setNotes("");
                                setPendingAction({ type: "reject", request });
                              }}>
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {request.reviewed_at ? formatDate(request.reviewed_at) : "—"}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <Dialog open={!!pendingAction} onOpenChange={(open) => !open && setPendingAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingAction?.type === "approve" ? "Restore this account?" : "Reject this request?"}
            </DialogTitle>
            <DialogDescription>
              {pendingAction?.type === "approve"
                ? "The account is restored immediately, with its original email address, and the person can sign in again."
                : "The account stays deleted. It can still be restored later, until its recovery window closes."}
            </DialogDescription>
          </DialogHeader>

          {pendingAction?.request.reason ? (
            <div className="rounded-md bg-muted p-3 text-sm">
              <div className="mb-1 font-medium">Their reason</div>
              <p className="text-muted-foreground">{pendingAction.request.reason}</p>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="notes">
              Notes {pendingAction?.type === "reject" ? "(required)" : "(optional)"}
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                pendingAction?.type === "approve"
                  ? "Anything worth recording about this decision"
                  : "Why this request is being rejected"
              }
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingAction(null)} disabled={isBusy}>
              Cancel
            </Button>
            <Button onClick={confirmAction} disabled={isBusy}>
              {isBusy
                ? "Working..."
                : pendingAction?.type === "approve"
                ? "Restore account"
                : "Reject request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default ReactivationRequestsPage;
